/**
 * Offline-fähiger Daten-Layer für Kalender-Einträge (Notiz / Frei / Urlaub /
 * Krank / Wunschfrei). Analog zu data/shifts.ts: Lese-Hooks live aus IndexedDB,
 * Mutationen optimistisch + Outbox.
 */

import { useLiveQuery } from 'dexie-react-hooks';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { db, type LocalCalendarEntry } from '../db/schema';
import * as outbox from '../db/outbox';
import type {
  CreateCalendarEntryInput,
  UpdateCalendarEntryInput,
} from '../lib/calendarEntries';
import { useAuth } from '../store/auth';

/** Live-Query: Kalender-Einträge des eingeloggten Users in einem Monat ("YYYY-MM"). */
export function useCalendarEntriesForMonth(month: string): LocalCalendarEntry[] | undefined {
  const me = useAuth((s) => s.user);
  return useLiveQuery(
    async () => {
      if (!me) return [];
      const start = `${month}-01`;
      const [y, m] = month.split('-').map(Number);
      const endDate = new Date(Date.UTC(y!, m!, 0)).toISOString().slice(0, 10);
      return db.calendarEntries
        .where('userId')
        .equals(me.id)
        .and((e) => !e.deletedAt && e.date >= start && e.date <= endDate)
        .sortBy('date');
    },
    [me?.id, month],
    [],
  );
}

function genUuid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  // Gesamtprüfung 11.09.2026: Fallback als gültige UUID v4 (der Server
  // verlangt eine UUID — „tmp-…“ wurde mit 400 abgelehnt und ging verloren).
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.floor(Math.random() * 16);
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

export function useCreateCalendarEntry() {
  const me = useAuth((s) => s.user);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateCalendarEntryInput) => {
      if (!me) throw new Error('Nicht eingeloggt');
      const tempId = genUuid();
      const now = new Date().toISOString();
      const type = input.type ?? 'note';
      const local: LocalCalendarEntry = {
        id: tempId,
        userId: input.userId ?? me.id,
        date: input.date,
        type,
        title: input.title ?? null,
        note: input.note ?? null,
        halfDay: input.halfDay ?? false,
        status: type === 'note' ? 'approved' : 'pending',
        createdBy: me.id,
        approvedBy: null,
        clientUuid: tempId,
        createdAt: now,
        lastModified: now,
        deletedAt: null,
        pending: true,
      };
      await db.calendarEntries.put(local);
      await outbox.enqueue('calendarEntry.create', tempId, { ...input, clientUuid: tempId });
      return local;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['outbox'] }),
  });
}

export function useUpdateCalendarEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; input: UpdateCalendarEntryInput }) => {
      const existing = await db.calendarEntries.get(args.id);
      if (!existing) throw new Error('Eintrag nicht im Cache');
      const updated: LocalCalendarEntry = {
        ...existing,
        ...args.input,
        title: args.input.title === undefined ? existing.title : args.input.title,
        note: args.input.note === undefined ? existing.note : args.input.note,
        lastModified: new Date().toISOString(),
        pending: true,
      };
      await db.calendarEntries.put(updated);
      const base = existing.pending ? undefined : existing.lastModified;
      await outbox.enqueue('calendarEntry.update', args.id, args.input, base);
      return updated;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['outbox'] }),
  });
}

export function useDeleteCalendarEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const existing = await db.calendarEntries.get(id);
      if (existing) {
        await db.calendarEntries.put({
          ...existing,
          deletedAt: new Date().toISOString(),
          pending: true,
        });
      }
      const base = existing && !existing.pending ? existing.lastModified : undefined;
      await outbox.enqueue('calendarEntry.delete', id, undefined, base);
      return id;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['outbox'] }),
  });
}
